import { Component } from '@theme/component';
import { isRevealed, reveal } from '@theme/r-reveal';
import { prefersReducedMotion, yieldToMainThread } from '@theme/utilities';

/* ↗ .cursor/references/code-notes/r-editorial-story.js.md#§1 */

/** Portion of an item that must enter the viewport before it reveals. */
const REVEAL_THRESHOLD = 0.15;

/** All connected stages (live reduced-motion preference resync). */
const instances = new Set();

export default class REditorialStory extends Component {
  /** @type {IntersectionObserver | undefined} */
  #observer;

  /** Re-derives after any mutation a morph can make: host arming, item attributes, subtree. */
  #mutations = new MutationObserver((records) => {
    if (this.#resyncing) return;
    for (const record of records) {
      if (record.type === 'childList' && record.addedNodes.length === 0 && record.removedNodes.length === 0) continue;
      this.#scheduleResync();
      return;
    }
  });

  /** True while resync() writes attributes, so its own writes never re-trigger it. */
  #resyncing = false;

  #resyncQueued = false;

  connectedCallback() {
    super.connectedCallback();
    instances.add(this);
    this.resync();
    this.#mutations.observe(this, {
      attributes: true,
      attributeFilter: ['data-r-armed', 'data-r-visible', 'data-r-reveal'],
      childList: true,
      subtree: true,
    });
  }

  disconnectedCallback() {
    this.#mutations.disconnect();
    this.#observer?.disconnect();
    this.#observer = undefined;
    instances.delete(this);
    super.disconnectedCallback();
  }

  /** Coalesces a burst of mutation records (a morph produces hundreds) into one resync. */
  #scheduleResync() {
    if (this.#resyncQueued) return;
    this.#resyncQueued = true;
    queueMicrotask(() => {
      this.#resyncQueued = false;
      if (this.isConnected) this.resync();
    });
  }

  updatedCallback() {
    super.updatedCallback();
    // A morph may have swapped items or reset attributes — re-derive.
    this.resync();
  }

  /** Re-derive arming and observation from the live DOM. */
  resync() {
    this.#resyncing = true;
    try {
      this.#observer?.disconnect();
      this.#observer = undefined;

      const items = this.querySelectorAll('[data-r-reveal]');
      const armed = items.length > 0 && !prefersReducedMotion();
      if (this.hasAttribute('data-r-armed') !== armed) this.toggleAttribute('data-r-armed', armed);

      if (!armed) {
        // Reduced motion (or no reveal items): everything is simply visible.
        for (const item of items) item.removeAttribute('data-r-visible');
        return;
      }

      this.#observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (!entry.isIntersecting) continue;
            this.#reveal(entry.target);
            this.#observer?.unobserve(entry.target);
          }
        },
        { threshold: REVEAL_THRESHOLD }
      );

      for (const item of items) {
        if (item.hasAttribute('data-r-visible')) continue;
        if (isRevealed(item)) {
          // Seen before; a morph stripped the end state. Put it straight back —
          // content the shopper has already read must never blank or re-enter.
          reveal(item);
          continue;
        }
        this.#observer.observe(item);
      }
    } finally {
      // The observer delivers records asynchronously; drain the ones this
      // resync just produced so they cannot schedule another.
      this.#mutations.takeRecords();
      this.#resyncing = false;
    }
  }

  /**
   * One-shot reveal. Layout settles first so the transition actually runs
   * (the jumbo-text / r-media entrance pattern). Goes through r-reveal's
   * shared primitive so the item joins the revealed set (see the contract).
   * @param {Element} item
   */
  #reveal(item) {
    yieldToMainThread().then(() => {
      if (item.isConnected) reveal(/** @type {HTMLElement} */ (item));
    });
  }
}

if (!customElements.get('r-editorial-story')) {
  customElements.define('r-editorial-story', REditorialStory);
}

// ↗ .cursor/references/code-notes/r-editorial-story.js.md#§2
matchMedia('(prefers-reduced-motion: reduce)').addEventListener('change', () => {
  for (const stage of instances) {
    if (stage instanceof REditorialStory) stage.resync();
  }
});
